'use client';

import React, { useState, useRef, useCallback, useMemo, useEffect, forwardRef } from 'react';
import classnames from 'classnames';

import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import Input from '../Input/Input';

import styles from './Typeahead.module.scss';

export type TypeaheadOption = {
  value: string;
  label: string;
};

const EMPTY_OPTIONS: TypeaheadOption[] = [];

export interface TypeaheadProps {
  name: string;
  label: string;
  placeholder: string;
  helperText?: string;
  theme?: 'dark' | 'light';
  direction?: 'ltr' | 'rtl';
  /** Static options for client-side filtering. Ignored when fetchOptions is provided. */
  options?: TypeaheadOption[];
  value?: string;
  disabled?: boolean;
  required?: boolean;
  debounceMs?: number;
  /** Supports both client-side (sync) and server-side (async) option resolution */
  fetchOptions?: (query: string) => TypeaheadOption[] | Promise<TypeaheadOption[]>;
  /** Minimum query length before filtering/fetching fires. Defaults to 3. */
  minQueryLength?: number;
  loadingText?: string;
  noResultsText?: string;
  /** Fires when the user selects an option, with the option's value */
  onSelect?: (value: string) => void;
  /** Fires when the input is explicitly cleared by the user */
  onChange?: (value: string) => void;
  onBlur?: () => void;
  validationResponse?: { validationType?: 'success' | 'warning' | 'error'; message?: string };
  dataTestId?: string;
  className?: string;
}

const Typeahead = forwardRef<HTMLInputElement, TypeaheadProps>(
  (
    {
      name,
      label,
      placeholder,
      helperText,
      theme = 'dark',
      direction = 'ltr',
      options = EMPTY_OPTIONS,
      value,
      disabled = false,
      required = false,
      debounceMs = 300,
      fetchOptions,
      minQueryLength = 3,
      loadingText = 'Loading...',
      noResultsText = 'No results found',
      onSelect,
      onChange,
      onBlur,
      validationResponse,
      dataTestId,
      className,
    },
    ref
  ) => {
    const [query, setQuery] = useState(value ?? '');
    const [isOpen, setIsOpen] = useState(false);
    const [asyncOptions, setAsyncOptions] = useState<TypeaheadOption[]>(() =>
      fetchOptions ? [] : options
    );
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listboxRef = useRef<HTMLUListElement>(null);
    const requestIdRef = useRef(0);
    const justSelectedRef = useRef(false);

    const listboxId = `${name}-listbox`;
    const getOptionId = useCallback((index: number) => `${name}-option-${index}`, [name]);

    useEffect(() => {
      if (value !== undefined) {
        if (justSelectedRef.current) {
          justSelectedRef.current = false;
          return;
        }
        setQuery(value);
      }
    }, [value]);

    useEffect(() => {
      if (!fetchOptions) {
        setAsyncOptions(options);
      }
    }, [options, fetchOptions]);

    useEffect(() => {
      if (!fetchOptions || !isOpen || disabled) return;
      if (query.trim().length < minQueryLength) {
        setAsyncOptions([]);
        return;
      }

      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      const timer = setTimeout(async () => {
        try {
          const nextOptions = await Promise.resolve(fetchOptions(query.trim()));
          if (requestId === requestIdRef.current) {
            setAsyncOptions(nextOptions);
          }
        } finally {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        }
      }, debounceMs);

      return () => clearTimeout(timer);
    }, [debounceMs, disabled, fetchOptions, isOpen, minQueryLength, query]);

    const filtered = useMemo(() => {
      if (fetchOptions) return asyncOptions;
      if (query.trim().length < minQueryLength) return options;
      if (!query.trim()) return options;
      const q = query.toLowerCase();
      return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [asyncOptions, fetchOptions, minQueryLength, options, query]);

    const showDropdown = isOpen && !disabled && query.trim().length >= minQueryLength;

    const handleSelect = useCallback(
      (option: TypeaheadOption) => {
        justSelectedRef.current = true;
        setQuery(option.label);
        setIsOpen(false);
        setActiveIndex(-1);
        onSelect?.(option.value);
      },
      [onSelect]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setIsOpen(true);
            setActiveIndex(0);
          }
          return;
        }
        if (e.key === 'Escape') {
          setIsOpen(false);
          setActiveIndex(-1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && activeIndex >= 0 && filtered[activeIndex]) {
          e.preventDefault();
          handleSelect(filtered[activeIndex]);
        }
      },
      [isOpen, filtered, activeIndex, handleSelect]
    );

    useEffect(() => {
      if (activeIndex < 0 || !listboxRef.current) return;
      const activeEl = document.getElementById(getOptionId(activeIndex));
      if (activeEl && typeof activeEl.scrollIntoView === 'function') {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }, [activeIndex, getOptionId]);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
          setIsOpen(false);
          setActiveIndex(-1);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const renderOptions = () => {
      if (isLoading) {
        return (
          <Box className={styles.typeaheadStatus}>
            <Typography variant="body" size="small" color="gray-3" theme={theme}>
              {loadingText}
            </Typography>
          </Box>
        );
      }
      if (filtered.length > 0) {
        return filtered.map((option, i) => (
          <li
            key={option.value}
            id={getOptionId(i)}
            className={classnames(styles.typeaheadOption, {
              [styles.typeaheadOptionActive]: i === activeIndex,
            })}
            role="option" // NOSONAR typescript:S6819
            aria-selected={i === activeIndex}
            tabIndex={-1}
            onClick={() => handleSelect(option)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleSelect(option);
            }}
          >
            <Typography variant="body" size="small" color="gray-1" theme={theme}>
              {option.label}
            </Typography>
          </li>
        ));
      }
      return (
        <Box className={styles.typeaheadStatus}>
          <Typography variant="body" size="small" color="gray-3" theme={theme}>
            {noResultsText}
          </Typography>
        </Box>
      );
    };

    return (
      <Box
        className={classnames(styles.typeaheadWrapper, styles[`theme-${theme}`], className)}
        ref={wrapperRef}
      >
        <div
          role="combobox" // NOSONAR typescript:S6819
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? getOptionId(activeIndex) : undefined}
          tabIndex={-1}
          className={styles.typeaheadCombobox}
        >
          <Input
            ref={ref}
            name={name}
            label={label}
            placeholder={placeholder}
            helperText={helperText}
            value={query}
            disabled={disabled}
            required={required}
            theme={theme}
            direction={direction}
            controlled
            showValidation={false}
            validationResponse={validationResponse}
            dataTestId={dataTestId ?? name}
            inputProps={{ 'aria-autocomplete': 'list' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const nextValue = e.target.value;
              setQuery(nextValue);
              setIsOpen(nextValue !== '');
              setActiveIndex(-1);
              if (nextValue === '') {
                onChange?.('');
              }
            }}
            onFocus={() => !disabled && setIsOpen(true)}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
          />
          {showDropdown && (
            <div className={classnames(styles.typeaheadDropdownWrapper, styles[`theme-${theme}`])}>
              <ul
                id={listboxId}
                role="listbox" // NOSONAR typescript:S6819
                aria-label={label}
                className={styles.typeaheadDropdown}
                ref={listboxRef}
              >
                {renderOptions()}
              </ul>
            </div>
          )}
        </div>
        {validationResponse?.message && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            <Typography
              className={classnames(
                styles.validationMessage,
                styles[`validation-${validationResponse.validationType}`]
              )}
              variant="body"
              size="extraSmall"
              theme={theme}
            >
              {validationResponse.message}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

Typeahead.displayName = 'Typeahead';

export default Typeahead;
